import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { getTranslation } from '../translations';
import './StoryReader.css';

export default function StoryReader({ user }) {
  const t = (key) => getTranslation(user?.language || 'en', key);
  const { id } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sending, setSending] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [regeneratingPage, setRegeneratingPage] = useState(null);
  const [regenerationStats, setRegenerationStats] = useState(null);
  const [showPromptInput, setShowPromptInput] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Interactive story state
  const [currentPath, setCurrentPath] = useState([]); // Track the path taken through choices
  const [availablePages, setAvailablePages] = useState([]); // Current available pages based on choices
  
  const isPro = user?.subscription_tier === 'pro';

  // Helper function to get truncated theme
  const getTruncatedTheme = () => {
    if (!story) return '';
    const theme = story.theme_description || story.theme;
    const maxLength = 80;
    if (theme && theme.length > maxLength) {
      return theme.substring(0, maxLength) + '...';
    }
    return theme;
  };

  useEffect(() => {
    fetchStory();
    if (isPro) {
      fetchRegenerationStats();
    }
  }, [id, isPro]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [id, currentPage]);

  // Setup interactive story navigation when story loads
  useEffect(() => {
    if (story) {
      console.log('🎮 Story loaded:', story.title);
      console.log('🎮 Is interactive:', story.is_interactive);
      console.log('🎮 Has choices:', story.choices?.length || 0);
      console.log('🎮 All pages:', story.pages?.map(p => ({ num: p.page_number, text: p.text.slice(0, 30) + '...' })));
      
      if (story.is_interactive && story.choices) {
        // Initialize available pages for interactive story
        initializeInteractiveStory();
      } else {
        // Regular story - all pages available
        setAvailablePages(story.pages || []);
      }
    }
  }, [story]);

  const initializeInteractiveStory = () => {
    if (!story?.is_interactive || !story?.pages) {
      console.log('🎮 Cannot initialize interactive story');
      return;
    }
    
    console.log('🎮 Initializing interactive story');
    
    // Start with pages 1-3 (before first choice)
    const initialPages = story.pages.filter(p => {
      const pageNum = typeof p.page_number === 'number' ? p.page_number : parseInt(p.page_number);
      const shouldInclude = !isNaN(pageNum) && pageNum <= 3;
      console.log(`🎮 Page ${p.page_number} (${pageNum}) included: ${shouldInclude}`);
      return shouldInclude;
    });
    
    console.log('🎮 Initial pages:', initialPages.map(p => p.page_number));
    setAvailablePages(initialPages);
    setCurrentPath([]);
  };

  const handleChoice = (choiceOption) => {
    console.log('🎮 === CHOICE CLICKED ===');
    console.log('🎮 Choice selected:', choiceOption);
    
    if (!story?.is_interactive) {
      console.log('🎮 ❌ Story is not interactive');
      return;
    }
    
    const nextPageId = choiceOption.next_page;
    console.log('🎮 Looking for next page ID:', nextPageId);
    console.log('🎮 All story pages:', story.pages.map(p => p.page_number));
    
    // Find the next page in the story
    const nextPage = story.pages.find(p => 
      p.page_number === nextPageId || p.page_number.toString() === nextPageId
    );
    
    console.log('🎮 Found next page:', nextPage);
    
    if (nextPage) {
      // Build the new available pages path
      const newAvailablePages = [...availablePages];
      
      // Add the chosen branch page (e.g., 4a or 4b)
      const existingPageIndex = newAvailablePages.findIndex(p => p.page_number === nextPage.page_number);
      if (existingPageIndex === -1) {
        newAvailablePages.push(nextPage);
        console.log('🎮 Added branch page:', nextPage.page_number);
      }
      
      // After a branch page, add continuation pages
      // For structure: [1,2,3] -> choice -> [4a or 4b] -> [5,6] -> choice -> [7a or 7b] -> [8,9,10]
      const branchPageNum = nextPage.page_number;
      
      // If this is page 4a or 4b, add pages 5 and 6 (convergence before next choice)
      if (branchPageNum === '4a' || branchPageNum === '4b') {
        const page5 = story.pages.find(p => p.page_number === 5);
        const page6 = story.pages.find(p => p.page_number === 6);
        if (page5 && !newAvailablePages.find(p => p.page_number === 5)) {
          newAvailablePages.push(page5);
          console.log('🎮 Added convergence page 5');
        }
        if (page6 && !newAvailablePages.find(p => p.page_number === 6)) {
          newAvailablePages.push(page6);
          console.log('🎮 Added convergence page 6');
        }
      }
      
      // If this is page 7a or 7b, add pages 8, 9, 10 (final convergence)
      if (branchPageNum === '7a' || branchPageNum === '7b') {
        for (let i = 8; i <= 10; i++) {
          const page = story.pages.find(p => p.page_number === i);
          if (page && !newAvailablePages.find(p => p.page_number === i)) {
            newAvailablePages.push(page);
            console.log(`🎮 Added final page ${i}`);
          }
        }
      }
      
      // Update available pages
      setAvailablePages(newAvailablePages);
      
      // Find the index of the next page in available pages
      const nextPageIndexInAvailable = newAvailablePages.findIndex(p => p.page_number === nextPage.page_number);
      const newCurrentPage = nextPageIndexInAvailable + 1; // +1 for cover page
      
      console.log('🎮 Setting current page to:', newCurrentPage);
      console.log('🎮 Available pages after choice:', newAvailablePages.map(p => p.page_number));
      
      // Navigate to next page
      setCurrentPage(newCurrentPage);
      setCurrentPath([...currentPath, choiceOption]);
      
      console.log('🎮 Navigation complete');
    } else {
      console.error('🎮 ❌ Could not find next page:', nextPageId);
    }
  };

  const fetchStory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStory(response.data);
    } catch (error) {
      toast.error(t('toasts.failedToLoadStory'));
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegenerationStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/regeneration-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegenerationStats(response.data);
    } catch (error) {
      console.error('Error fetching regeneration stats:', error);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/stories/${id}/export-pdf`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${story.title}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t('toasts.pdfDownloaded'));
    } catch (error) {
      toast.error(t('toasts.failedToExportPdf'));
    } finally {
      setExporting(false);
    }
  };

  const handleSendToKindle = async () => {
    if (!isPro) {
      setShowUpgradeModal(true);
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      console.log('Sending story to Kindle:', id);
      const response = await axios.post(`${API}/stories/${id}/send-kindle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Send to Kindle response:', response.data);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Send to Kindle error:', error);
      toast.error(error.response?.data?.detail || t('toasts.failedToSendKindle'));
    } finally {
      setSending(false);
    }
  };

  const handlePrint = () => {
    if (!isPro) {
      setShowUpgradeModal(true);
      return;
    }
    try {
      // Ensure page is ready before printing
      if (window.print) {
        window.print();
      } else {
        toast.error(t('toasts.printNotAvailable'));
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error(t('toasts.failedToPrint'));
    }
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleRegenerateClick = () => {
    if (!isPro) {
      setShowUpgradeModal(true);
      return;
    }
    
    // Show prompt input field
    setShowPromptInput(true);
  };

  const handleRegeneratePage = async (pageNumber) => {
    if (regeneratingPage) {
      toast.error(user?.language === 'de' ? 'Bitte warten Sie, bis die aktuelle Regenerierung abgeschlossen ist' : 'Please wait for current regeneration to complete');
      return;
    }

    setRegeneratingPage(pageNumber);
    setShowPromptInput(false);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/stories/${id}/regenerate-page/${pageNumber}`,
        { custom_prompt: customPrompt || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { regenerations_used, regenerations_remaining } = response.data;
      
      toast.success(
        user?.language === 'de' 
          ? `Einzelbild erfolgreich ausgetauscht! ${regenerations_remaining}/10 übrig` 
          : `Image replaced successfully! ${regenerations_remaining}/10 remaining`
      );
      
      // Clear prompt and refresh
      setCustomPrompt('');
      await fetchStory();
      await fetchRegenerationStats();
      
    } catch (error) {
      console.error('Regeneration error:', error);
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      } else if (error.response?.status === 429) {
        toast.error(
          user?.language === 'de' 
            ? 'Monatliches Limit erreicht (10/Monat). Zurückgesetzt am 1. des nächsten Monats.' 
            : 'Monthly limit reached (10/month). Resets on 1st of next month.'
        );
      } else {
        toast.error(error.response?.data?.detail || (user?.language === 'de' ? 'Fehler beim Regenerieren des Bildes' : 'Failed to regenerate image'));
      }
    } finally {
      setRegeneratingPage(null);
    }
  };

  if (loading) {
    return (
      <div className="story-reader">
        <div className="loading-container">
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  if (!story) return null;

  const isCover = currentPage === 0;
  const displayPages = story.is_interactive ? availablePages : story.pages;
  
  // Calculate total pages for the complete story path
  let totalPages;
  if (story.is_interactive && story.pages?.length > 0) {
    // For interactive stories, calculate the full path length
    // Path structure: [1,2,3] + [4a OR 4b] + [5,6] + [7a OR 7b] + [8,9,10] = 10 pages
    // Non-branch pages are those without 'a' or 'b' suffix
    const nonBranchPages = story.pages.filter(p => {
      const pageNum = String(p.page_number);
      return !pageNum.endsWith('a') && !pageNum.endsWith('b');
    }).length;
    
    // Each choice point adds one branch page to the path
    const numChoices = story.choices?.length || 0;
    
    // Total story pages (excluding cover) = non-branch pages + one branch per choice
    const storyPagesCount = nonBranchPages + numChoices;
    totalPages = storyPagesCount + 1; // +1 for cover page
    
    console.log('🎮 Interactive Story Total Pages:', {
      allPagesCount: story.pages?.length,
      nonBranchPages,
      numChoices,
      storyPagesCount,
      totalWithCover: totalPages
    });
  } else {
    // Regular story: all pages are available
    totalPages = (story.pages?.length || displayPages.length) + 1; // +1 for cover
  }
  
  const currentStoryPage = currentPage > 0 ? displayPages[currentPage - 1] : null;
  
  // Check if current page has choices (for interactive stories)
  const currentPageChoices = story.is_interactive && story.choices 
    ? story.choices.find(choice => {
        // For interactive stories, after_page corresponds to the actual page number, not the index
        const actualPageNumber = currentStoryPage?.page_number;
        console.log('🎮 Looking for choices after page:', actualPageNumber, 'type:', typeof actualPageNumber, 'Available choices:', story.choices);
        // Use loose equality or convert to same type for comparison
        return choice.after_page == actualPageNumber || 
               String(choice.after_page) === String(actualPageNumber);
      })
    : null;
  
  console.log('🎮 Current page choices:', currentPageChoices);

  return (
    <div className="story-reader">
      <div className="reader-header no-print">
        <div className="mobile-reader-toolbar">
          <Button
            data-testid="mobile-back-button"
            onClick={() => {
              closeMobileMenu();
              navigate('/dashboard');
            }}
            className="action-button action-button-secondary mobile-toolbar-back"
          >
            {user?.language === 'de' ? 'Zurück' : 'Back'}
          </Button>
          <Button
            data-testid="mobile-menu-button"
            onClick={() => setMobileMenuOpen((open) => !open)}
            className="action-button action-button-ghost mobile-menu-trigger"
            aria-expanded={mobileMenuOpen}
            aria-label={user?.language === 'de' ? 'Menü öffnen' : 'Open menu'}
          >
            <span className="mobile-menu-icon" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </span>
            <span>{user?.language === 'de' ? 'Menü' : 'Menu'}</span>
          </Button>
        </div>
        {mobileMenuOpen && (
          <div className="mobile-reader-menu">
            {isPro && (
              <Button
                data-testid="mobile-replace-image-button"
                onClick={() => {
                  handleRegenerateClick();
                  closeMobileMenu();
                }}
                disabled={regeneratingPage !== null || (regenerationStats && regenerationStats.remaining <= 0)}
                className="action-button action-button-primary mobile-menu-action"
                title={user?.language === 'de' ? 'Einzelbild austauschen (Pro)' : 'Replace single image (Pro)'}
              >
                <span>
                  {regeneratingPage === currentPage
                    ? (user?.language === 'de' ? 'Bild wird ersetzt' : 'Replacing image')
                    : (user?.language === 'de' ? 'Bild austauschen' : 'Replace image')}
                </span>
                {regenerationStats && (
                  <span className="action-button-meta">
                    {regenerationStats.remaining}/{regenerationStats.limit}
                  </span>
                )}
              </Button>
            )}
            <Button
              data-testid="mobile-export-pdf-button"
              onClick={() => {
                handleExportPDF();
                closeMobileMenu();
              }}
              disabled={exporting}
              className="action-button action-button-ghost mobile-menu-action"
            >
              {exporting
                ? (user?.language === 'de' ? 'PDF wird exportiert' : 'Exporting PDF')
                : (user?.language === 'de' ? 'PDF exportieren' : 'Export PDF')}
            </Button>
            <Button
              data-testid="mobile-print-button"
              onClick={() => {
                handlePrint();
                closeMobileMenu();
              }}
              className="action-button action-button-ghost mobile-menu-action"
            >
              {user?.language === 'de' ? 'Drucken' : 'Print'}
            </Button>
            <Button
              data-testid="mobile-send-kindle-button"
              onClick={() => {
                handleSendToKindle();
                closeMobileMenu();
              }}
              disabled={sending}
              className="action-button action-button-ghost mobile-menu-action"
            >
              {sending
                ? (user?.language === 'de' ? 'Wird gesendet' : 'Sending')
                : (user?.language === 'de' ? 'An Kindle senden' : 'Send to Kindle')}
            </Button>
          </div>
        )}
        <div className="header-actions">
          <div className="toolbar-group toolbar-group-primary">
            <Button 
              data-testid="back-button"
              onClick={() => navigate('/dashboard')}
              className="action-button action-button-secondary"
            >
              {user?.language === 'de' ? 'Zurück' : 'Back'}
            </Button>
            {isPro && (
              <Button 
                data-testid="replace-image-button"
                onClick={handleRegenerateClick}
                disabled={regeneratingPage !== null || (regenerationStats && regenerationStats.remaining <= 0)}
                className="action-button action-button-primary"
                title={user?.language === 'de' ? 'Einzelbild austauschen (Pro)' : 'Replace single image (Pro)'}
              >
                {regeneratingPage === currentPage
                  ? (user?.language === 'de' ? 'Bild wird ersetzt' : 'Replacing image')
                  : (user?.language === 'de' ? 'Bild austauschen' : 'Replace image')}
                {regenerationStats && (
                  <span className="action-button-meta">
                    {regenerationStats.remaining}/{regenerationStats.limit}
                  </span>
                )}
              </Button>
            )}
          </div>
          <div className="toolbar-group toolbar-group-utility">
            <Button 
              data-testid="export-pdf-button"
              onClick={handleExportPDF} 
              disabled={exporting}
              className="action-button action-button-ghost"
            >
              {exporting
                ? (user?.language === 'de' ? 'PDF wird exportiert' : 'Exporting PDF')
                : (user?.language === 'de' ? 'PDF exportieren' : 'Export PDF')}
            </Button>
            <Button 
              data-testid="print-button"
              onClick={handlePrint}
              className="action-button action-button-ghost"
            >
              {user?.language === 'de' ? 'Drucken' : 'Print'}
            </Button>
            <Button 
              data-testid="send-kindle-button"
              onClick={handleSendToKindle}
              disabled={sending}
              className="action-button action-button-ghost"
            >
              {sending
                ? (user?.language === 'de' ? 'Wird gesendet' : 'Sending')
                : (user?.language === 'de' ? 'An Kindle senden' : 'Send to Kindle')}
            </Button>
          </div>
        </div>
      </div>

      {/* Prompt input for image replacement - shown below header when active */}
      {showPromptInput && isPro && (
        <div className="prompt-input-bar no-print">
          <span className="prompt-input-label">
            {user?.language === 'de' ? 'Bild für Seite ' : 'Image for page '}{currentPage + 1}:
          </span>
          <input
            type="text"
            className="prompt-input"
            placeholder={user?.language === 'de' ? 'Beschreiben Sie das gewünschte Bild (optional)...' : 'Describe the desired image (optional)...'}
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            disabled={regeneratingPage !== null}
          />
          <Button
            onClick={() => handleRegeneratePage(currentPage)}
            disabled={regeneratingPage !== null}
            className="prompt-inline-button prompt-inline-confirm"
          >
            {user?.language === 'de' ? 'Bestätigen' : 'Confirm'}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setShowPromptInput(false);
              setCustomPrompt('');
            }}
            className="prompt-inline-button prompt-inline-cancel"
          >
            {user?.language === 'de' ? 'Abbrechen' : 'Cancel'}
          </Button>
        </div>
      )}

      <div className="reader-content">
        {/* Print version: all pages */}
        <div className="print-only">
          <div className="story-book">
            <div className="cover-page">
              {(story.cover_image_url || story.cover_image_base64) && (
                <img 
                  src={story.cover_image_url || `data:image/png;base64,${story.cover_image_base64}`} 
                  alt="Story cover"
                  className="cover-image"
                />
              )}
              <h1 className="story-title">{story.title}</h1>
              <p className="story-subtitle">
                A special story for {story.child_name}
              </p>
              <p className="story-meta">
                Age {story.age} • {getTruncatedTheme()}
              </p>
            </div>
          </div>

          {story.pages.map((page, index) => (
            <div key={index} className="story-book">
              <div className="story-page">
                {(page.image_url || page.image_base64) && (
                  <div className="page-image">
                    <img 
                      src={page.image_url || `data:image/png;base64,${page.image_base64}`} 
                      alt={`Page ${page.page_number}`}
                    />
                  </div>
                )}
                <div className="page-text">
                  <p>{page.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Screen version: one page at a time */}
        <div className="story-book no-print">
          {isCover ? (
            <div className="cover-page" data-testid="story-cover-page">
              {(story.cover_image_url || story.cover_image_base64) && (
                <img 
                  src={story.cover_image_url || `data:image/png;base64,${story.cover_image_base64}`} 
                  alt="Story cover"
                  className="cover-image"
                />
              )}
              <h1 className="story-title" data-testid="story-title-display">{story.title}</h1>
              <p className="story-subtitle">
                A special story for {story.child_name}
              </p>
              <p className="story-meta">
                Age {story.age} • {getTruncatedTheme()}
              </p>
            </div>
          ) : (
            <div key={`page-${currentPage}-${currentStoryPage?.page_number || currentPage}`} className="story-page" data-testid={`story-page-${currentPage}`}>
              {currentStoryPage && (
                <>
                  {(currentStoryPage.image_url || currentStoryPage.image_base64) && (
                    <div className="page-image-container">
                      <div className="page-image">
                        <img 
                          src={currentStoryPage.image_url || `data:image/png;base64,${currentStoryPage.image_base64}`} 
                          alt={`Page ${currentPage}`}
                        />
                      </div>
                    </div>
                  )}
                  <div className="page-text">
                    <p data-testid={`page-text-${currentPage}`}>
                      {currentStoryPage.text}
                    </p>
                  </div>
                  
                  {/* Interactive Story Choices - BELOW the text */}
                  {currentPageChoices && (
                    <div className="story-choices">
                      <h3 className="choice-question">
                        {currentPageChoices.question}
                      </h3>
                      <div className="choice-options">
                        {currentPageChoices.options.map((option, index) => (
                          <button
                            key={index}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleChoice(option);
                            }}
                            onMouseDown={() => console.log('🎮 Mouse down on button', index)}
                            className="choice-button"
                          >
                            {String.fromCharCode(65 + index)}. {option.text}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="reader-controls no-print">
          <Button
            data-testid="prev-page-button"
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            variant="outline"
            className="reader-nav-button"
          >
            ← Previous
          </Button>
          
          <div key={`indicator-${currentPage}-${currentStoryPage?.page_number || 0}`} className="page-indicator" data-testid="page-indicator">
            {isCover 
              ? 'Cover' 
              : `Page ${currentPage} of ${totalPages - 1}`
            }
          </div>
          
          <Button
            data-testid="next-page-button"
            onClick={() => setCurrentPage(Math.min(displayPages.length, currentPage + 1))}
            disabled={currentPage === displayPages.length}
            variant="outline"
            className="reader-nav-button"
          >
            Next →
          </Button>
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="upgrade-modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
            <h2>⭐ Upgrade to Pro</h2>
            <p>This feature is only available for Pro subscribers.</p>
            <ul className="feature-list">
              <li>✓ Print your stories</li>
              <li>✓ Send stories to Kindle</li>
              <li>✓ 4 stories per month</li>
              <li>✓ Premium illustrations</li>
            </ul>
            <div className="modal-actions">
              <Button 
                onClick={() => navigate('/pricing')}
                className="upgrade-modal-button"
              >
                View Plans
              </Button>
              <Button 
                onClick={() => setShowUpgradeModal(false)}
                variant="outline"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
